import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { RESPONSIVE_BREAKPOINTS, type ResponsiveDevice } from './breakpoints';

/**
 * Prefer VisualViewport for iPad/iOS so height reflects toolbar collapse/expand.
 */
const getViewportDimensions = (): { width: number; height: number } => {
  if (typeof window === 'undefined') return { width: 0, height: 0 };
  const vv = (window as any).visualViewport as VisualViewport | undefined;
  const width = vv?.width ?? window.innerWidth;
  const height = vv?.height ?? window.innerHeight;
  return { width: Math.round(width), height: Math.round(height) };
};

export interface ResponsiveState {
  width: number;
  height: number;
  device: ResponsiveDevice;
  isIPhone: boolean;
  isIPad: boolean;
  isDesktop: boolean;
  isPortrait: boolean;
  isLandscape: boolean;
}

const defaultState: ResponsiveState = {
  width: 0,
  height: 0,
  device: 'desktop',
  isIPhone: false,
  isIPad: false,
  isDesktop: true,
  isPortrait: true,
  isLandscape: false,
};

const ResponsiveContext = createContext<ResponsiveState>(defaultState);

const getDeviceFromWidth = (width: number): ResponsiveDevice => {
  if (width <= RESPONSIVE_BREAKPOINTS.iphoneMaxWidth) {
    return 'iphone';
  }
  if (width <= RESPONSIVE_BREAKPOINTS.ipadMaxWidth) {
    return 'ipad';
  }
  return 'desktop';
};

const detectIPadUA = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iPadRegex = /iPad|iPadOS/i;
  const macTouch = /Macintosh/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1;
  return iPadRegex.test(ua) || macTouch;
};

const detectAndroidTabletUA = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Android/i.test(ua) && !/Mobile/i.test(ua);
};

const detectIPhoneUA = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /iPhone/i.test(ua);
};

const createResponsiveState = (dimensions: { width: number; height: number }): ResponsiveState => {
  const { width, height } = dimensions;
  const deviceFromWidth = getDeviceFromWidth(width);

  const isIPadUA = detectIPadUA();
  const isAndroidTabletUA = detectAndroidTabletUA();
  const isIPhoneUA = detectIPhoneUA();

  const device: ResponsiveDevice = isIPadUA || isAndroidTabletUA
    ? 'ipad'
    : isIPhoneUA
      ? 'iphone'
      : deviceFromWidth;

  const isIPhone = device === 'iphone';
  const isIPad = device === 'ipad';
  const isDesktop = device === 'desktop';
  const isPortrait = height >= width;

  return {
    width,
    height,
    device,
    isIPhone,
    isIPad,
    isDesktop,
    isPortrait,
    isLandscape: !isPortrait,
  };
};

interface ResponsiveProviderProps {
  children: React.ReactNode;
  /**
   * Optional width used when rendering on the server or tests.
   */
  ssrWidth?: number;
  /**
   * Optional height used when rendering on the server or tests.
   */
  ssrHeight?: number;
}

export const ResponsiveProvider: React.FC<ResponsiveProviderProps> = ({
  children,
  ssrWidth = RESPONSIVE_BREAKPOINTS.ipadMaxWidth + 1,
  ssrHeight = RESPONSIVE_BREAKPOINTS.ipadMaxWidth,
}) => {
  const initialState = useMemo(() => {
    if (typeof window === 'undefined') {
      return createResponsiveState({ width: ssrWidth, height: ssrHeight });
    }
    const dims = getViewportDimensions();
    return createResponsiveState(dims);
  }, [ssrWidth, ssrHeight]);

  const [state, setState] = useState<ResponsiveState>(initialState);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => undefined;
    }

    const handleResize = () => {
      const dims = getViewportDimensions();
      setState(createResponsiveState(dims));
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    // Add VisualViewport listeners for iOS/iPadOS to track toolbar collapse/expand
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    if (vv) {
      vv.addEventListener('resize', handleResize, { passive: true });
      // iOS fires scroll on vv during toolbar collapse/expand, which changes vv.height
      vv.addEventListener('scroll', handleResize, { passive: true });
    }

    // sync once on mount in case initial SSR/default differs from real viewport
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      if (vv) {
        vv.removeEventListener('resize', handleResize);
        vv.removeEventListener('scroll', handleResize);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.body.dataset.device = state.device;
    document.body.dataset.orientation = state.isPortrait ? 'portrait' : 'landscape';

    const targetLandscapeWidth = 1180;
    // Landscape emulation is for iPad portrait only.
    // iPhone has its own mobile CSS — applying transform scale to iPhone breaks
    // media query breakpoints (layout sees 591px instead of 414px).
    const shouldEmulateLandscape =
      state.isIPad && state.isPortrait && state.width < targetLandscapeWidth;

    if (shouldEmulateLandscape) {
      document.body.classList.add('landscape-emulation');
      const scale = Math.max(0.7, Math.min(1, state.width / targetLandscapeWidth));
      const fontScale = Math.max(0.8, Math.min(1, scale + 0.05));
      document.body.style.setProperty('--landscape-scale', scale.toString());
      document.body.style.setProperty('--landscape-font-scale', fontScale.toString());
      // Lock outer scroll — layout box is (1/scale)×100vh which would create a scrollbar
      document.documentElement.classList.add('landscape-scroll-lock');
    } else {
      document.body.classList.remove('landscape-emulation');
      document.body.style.removeProperty('--landscape-scale');
      document.body.style.removeProperty('--landscape-font-scale');
      document.documentElement.classList.remove('landscape-scroll-lock');
    }

    // Notebook/MacBook zoom: scale to 80% on screens 1025–1699px (laptop, not full-HD desktop)
    const NOTEBOOK_MIN = 1025;
    const NOTEBOOK_MAX = 1699;
    const isNotebook =
      state.device === 'desktop' &&
      state.width >= NOTEBOOK_MIN &&
      state.width <= NOTEBOOK_MAX;

    if (isNotebook) {
      document.body.classList.add('notebook-zoom');
      document.body.style.setProperty('--notebook-scale', '0.8');
      // Lock outer scroll so the 125vh layout box doesn't create a body scrollbar
      document.documentElement.classList.add('notebook-scroll-lock');
    } else {
      document.body.classList.remove('notebook-zoom');
      document.body.style.removeProperty('--notebook-scale');
      document.documentElement.classList.remove('notebook-scroll-lock');
    }

    if (typeof document !== 'undefined') {
      const root = document.documentElement;
      // When notebook-zoom or landscape-emulation is active, CSS px values inside the zoomed
      // container render at physical size multiplied by scale. To keep table scroll heights filling the
      // visible viewport correctly, we report --viewport-height as height/scale so that
      // calc(var(--viewport-height) - Npx) * scale ≈ actual visible height - offset.
      let effectiveHeight = state.height;

      if (isNotebook) {
        effectiveHeight = Math.round(state.height / 0.8);
      } else if (shouldEmulateLandscape) {
        const scale = Math.max(0.7, Math.min(1, state.width / targetLandscapeWidth));
        effectiveHeight = Math.round(state.height / scale);
      }

      // System/property banners sit outside the zoom container and consume real px.
      // Subtract their height (converted to effective units) so that all
      // calc(var(--viewport-height) - Npx) table-scroll formulas stay correct.
      const realBannerH = parseFloat(root.style.getPropertyValue('--banner-height') || '') || 0;
      if (realBannerH > 0) {
        const activeScale = isNotebook ? 0.8
          : shouldEmulateLandscape ? Math.max(0.7, Math.min(1, state.width / targetLandscapeWidth))
          : 1;
        effectiveHeight = Math.max(200, Math.round(effectiveHeight - realBannerH / activeScale));
      }

      root.style.setProperty('--viewport-width', `${state.width}px`);
      root.style.setProperty('--viewport-height', `${effectiveHeight}px`);
      // 1vh equivalent so CSS can do height: calc(var(--vh) * 100) as a fallback
      root.style.setProperty('--vh', `${effectiveHeight / 100}px`);
      root.style.setProperty('--vw', `${state.width / 100}px`);
    }
  }, [state.device, state.isPortrait, state.width, state.height]);

  return <ResponsiveContext.Provider value={state}>{children}</ResponsiveContext.Provider>;
};

export const useResponsiveContext = (): ResponsiveState => {
  const context = useContext(ResponsiveContext);
  if (!context) {
    throw new Error('useResponsiveContext must be used within ResponsiveProvider');
  }
  return context;
};
